import React from 'react';
import { Box, Typography } from '@mui/material';
import { AdminBookingReportVO } from '../../../types/model/valueObject/booking/AdminBookingReportVO';
import StatusBadge from '../../StatusBadge';
import { formatDurationType } from '../../../util/BookingUtil';
import { formatCurrencyValue } from '../../../util/PaymentUtils';
import { formatBookingTimestamp, getBrowserTimezone } from '../../../util/DateUtils';
import {
  BookingReasonDeclinedLabel,
  BookingStatus,
  BookingSubscriptionStatus
} from '../../constants/securspace-constants';

const FILLER_VALUE = 'N/A';
const MAX_COLUMNS = 4;
const MIN_COLUMN_WIDTH_PX = 150;
const COLUMN_GAP_PX = 16;

// Fills the row up to MAX_COLUMNS, but drops to fewer columns (down to 1) whenever a cell
// would be narrower than MIN_COLUMN_WIDTH_PX so text never overflows its cell.
const GRID_TEMPLATE_COLUMNS =
  `repeat(auto-fill, minmax(max(${MIN_COLUMN_WIDTH_PX}px, calc((100% - ${(MAX_COLUMNS - 1) * COLUMN_GAP_PX}px) / ${MAX_COLUMNS})), 1fr))`;

interface AdminBookingDetailsBodyProps {
  item: AdminBookingReportVO;
}

interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
}

interface DetailFieldProps {
  label: string;
  value: React.ReactNode;
  mobileOnly?: boolean;
}

const hasValue = (value: React.ReactNode) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const SUBSCRIPTION_STATUS_BADGES: string[] = [
  BookingSubscriptionStatus.ACTIVE,
  BookingSubscriptionStatus.CANCELLED,
  BookingSubscriptionStatus.DECLINED,
];

// Active renders green and Cancelled/Declined red (StatusBadge); "None" or a blank value is N/A with no chip.
const renderSubscriptionStatus = (status?: string): React.ReactNode => {
  const normalized = status?.trim().toLowerCase();
  if (!normalized || normalized === BookingSubscriptionStatus.NONE.toLowerCase()) return null;
  const badgeLabel = SUBSCRIPTION_STATUS_BADGES.find(label => label.toLowerCase() === normalized);
  // StatusBadge is a block element, so wrap it to keep the chip only as wide as its text and center its text vertically.
  return badgeLabel ? <Box sx={{ width: 'fit-content', '& > div': { display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 0 } }}>
      <StatusBadge>{badgeLabel}</StatusBadge>
    </Box> : status;
};

const DetailSection = ({ title, children }: DetailSectionProps) => (
  <Box component="section" sx={{ mb: 3 }}>
    <Typography
      component="h3"
      sx={{
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'primary.main',
        borderBottom: '2px solid #e8eaf6',
        pb: 0.75,
        mb: 1.75,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        columnGap: `${COLUMN_GAP_PX}px`,
        rowGap: 2.25,
      }}
    >
      {children}
    </Box>
  </Box>
);

const DetailField = ({ label, value, mobileOnly = false }: DetailFieldProps) => (
  <Box
    sx={{
      minWidth: 0,
      overflowWrap: 'anywhere',
      display: mobileOnly ? { xs: 'block', sm: 'none' } : 'block',
    }}
  >
    <Typography
      component="div"
      sx={{
        fontSize: '0.7rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        color: 'rgba(0, 0, 0, 0.6)',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography component="div" sx={{ fontSize: '0.95rem', fontWeight: 500, color: 'text.primary' }}>
      {hasValue(value) ? value : FILLER_VALUE}
    </Typography>
  </Box>
);

/**
 * Grouped details shown in the expanded body of an admin booking card.
 * @param item the AdminBookingReportVO to display
 */
const AdminBookingDetailsBody = ({ item }: AdminBookingDetailsBodyProps) => {
  const timezone = getBrowserTimezone();
  const showDeclinedNote =
    item?.status === BookingStatus.DECLINED &&
    item?.reasonDeclined !== BookingReasonDeclinedLabel.AUTO_DECLINED;

  return (
    <Box sx={{ width: '100%', minWidth: 0, pr: { sm: 5 } }}>
      <DetailSection title="Booking Overview">
        {/* Shown in the summary card from sm up; remains mobile-only here (issue #3362) */}
        <DetailField label="Duration Type" value={formatDurationType(item?.durationType)} mobileOnly />
        <DetailField label="Brokered" value={item?.brokered} />
        <DetailField label="Extended/Date Adjusted" value={item?.endDateAdjusted} />
        <DetailField label="Equipment Type" value={item?.assetType} />
        <DetailField label="Created On" value={hasValue(item?.creationDate) ? formatBookingTimestamp(item.creationDate, timezone) : null} />
        <DetailField label="Subscription Status" value={renderSubscriptionStatus(item?.bookingSubscriptionStatus)} />
        <DetailField label="Charge Overages" value={item?.chargeOverages} />
        <DetailField label="Overage Rate" value={item?.overageRate} />
        <DetailField label="Reason Declined" value={item?.reasonDeclined} />
        {showDeclinedNote && <DetailField label="Reason Declined Note" value={item?.reasonDeclinedNote} />}
      </DetailSection>

      <DetailSection title="Location Info">
        <DetailField label="Location" value={item?.locationName} />
        <DetailField label="Address" value={item?.locationAddress} />
        <DetailField label="State" value={item?.locationState} />
        <DetailField label="GMS Yard" value={item?.gmsYard} />
      </DetailSection>

      <DetailSection title="Financial Details">
        <DetailField label="Rate Per Space" value={formatCurrencyValue(item?.buyerRatePerSpace, true)} />
        <DetailField label="Total Customer Amount" value={formatCurrencyValue(item?.initialCharge, true)} />
        <DetailField label="SecurSpace Percentage" value={item?.securSpacePercentage} />
      </DetailSection>

      <DetailSection title="Payment Breakdown">
        <DetailField label="Initial Payment Amount" value={formatCurrencyValue(item?.initialPaymentAmount, true)} />
        <DetailField label="Initial SecurSpace Amount" value={formatCurrencyValue(item?.initialSecurSpaceFeeAmount, true)} />
        <DetailField label="Initial Payment Processor Fee" value={formatCurrencyValue(item?.initialPaymentProcessorFee, true)} />
        <DetailField label="Initial Supplier Amount" value={formatCurrencyValue(item?.initialSupplierPayoutAmount, true)} />
      </DetailSection>

      <DetailSection title="Account Details">
        <DetailField label="Supplier Email Address" value={item?.supplierEmail} />
        <DetailField label="Buyer Email Address" value={item?.buyerEmail} />
      </DetailSection>
    </Box>
  );
};

export { AdminBookingDetailsBody };
